import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import CollapseHeader from "../../../../core/common/collapse-header";
import { AuthContext } from "../../../../context/authcontext";
import { useSalesUserId } from "../../../../utils/salesUserScope";
import { ExportDropdown, ExportKind } from "../../Dashboard/common";
import { downloadExportFile } from "../../Dashboard/dashboardApi";
import { exportDashboardPdf } from "../../Dashboard/exportDashboardPdf";
import FilterChips, { FilterChipItem } from "../../../../components/filters/FilterChips";
import "./activitiesReport.css";
import {
  CrmEntity,
  SalesActivityDashboardResponse,
  fetchAllowedCrmEntities,
  fetchSalesActivityDashboard,
  exportSalesActivityExcel,
  flattenActivityByEntity,
} from "./activitiesReportService";
import ActivitiesReportFilters from "./ActivitiesReportFilters";
import ActivityScoreCards from "./ActivityScoreCards";
import ActivityTypeChart from "./ActivityTypeChart";
import ActivityStatusChart from "./ActivityStatusChart";
import ActivityEntityBreakdownChart from "./ActivityEntityBreakdownChart";

const toIsoOrNull = (date: Date | null) => (date ? date.toISOString() : null);

const DEFAULT_RANGE_LABEL = "Last 30 Days";

const createDefaultRange = (): [Date, Date] => [
  dayjs().subtract(30, "day").startOf("day").toDate(),
  dayjs().endOf("day").toDate(),
];

const formatCompactRange = (start: Date, end: Date) => {
  const from = dayjs(start);
  const to = dayjs(end);
  return from.year() === to.year()
    ? `${from.format("DD MMM")} – ${to.format("DD MMM YYYY")}`
    : `${from.format("DD MMM YYYY")} – ${to.format("DD MMM YYYY")}`;
};

const ChartLoadingSkeleton: React.FC<{
  variant: "bars" | "donut" | "grid";
  height: number;
}> = ({ variant, height }) => {
  if (variant === "donut") {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height }}
      >
        <div
          className="ar-skeleton"
          style={{ width: height * 0.62, height: height * 0.62, borderRadius: "50%" }}
        />
      </div>
    );
  }
  if (variant === "grid") {
    return (
      <div style={{ height }} className="d-flex flex-column gap-2 justify-content-center">
        {[0, 1, 2, 3].map((row) => (
          <div key={row} className="d-flex gap-2">
            {[0, 1, 2, 3, 4, 5, 6].map((col) => (
              <div key={col} className="ar-skeleton" style={{ flex: 1, height: 34 }} />
            ))}
          </div>
        ))}
      </div>
    );
  }
  const bars = [0.9, 0.55, 0.75, 0.35, 0.6];
  return (
    <div className="ar-chart-skeleton" style={{ height }}>
      {bars.map((h, i) => (
        <span key={i} className="ar-skeleton" style={{ height: `${h * 100}%` }} />
      ))}
    </div>
  );
};

const ActivitiesReport = () => {
  const { selectedTenantId } = useContext(AuthContext);
  const salesUserId = useSalesUserId();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [defaultRange] = useState<[Date, Date]>(createDefaultRange);
  const hasCustomDateRange = !!dateRange[0] && !!dateRange[1];
  const rangeStart = hasCustomDateRange ? (dateRange[0] as Date) : defaultRange[0];
  const rangeEnd = hasCustomDateRange ? (dateRange[1] as Date) : defaultRange[1];
  const [crmEntityId, setCrmEntityId] = useState<number | null>(null);
  const [crmSalesActivityTypeId, setCrmSalesActivityTypeId] = useState<
    number | null
  >(null);
  const [crmSalesActivityTypeStatusId, setCrmSalesActivityTypeStatusId] =
    useState<number | null>(null);

  const [entityOptions, setEntityOptions] = useState<CrmEntity[]>([]);
  const [overview, setOverview] = useState<SalesActivityDashboardResponse | null>(
    null
  );
  const [filtered, setFiltered] = useState<SalesActivityDashboardResponse | null>(
    null
  );
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [filteredLoading, setFilteredLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<ExportKind | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const exportFilters = useMemo(
    () => ({
      startDatetime: toIsoOrNull(rangeStart),
      endDatetime: toIsoOrNull(rangeEnd),
      crmEntityId,
      salesUserId,
      crmSalesActivityTypeId,
      crmSalesActivityTypeStatusId,
    }),
    [rangeStart, rangeEnd, crmEntityId, salesUserId, crmSalesActivityTypeId, crmSalesActivityTypeStatusId]
  );

  useEffect(() => {
    fetchAllowedCrmEntities()
      .then(setEntityOptions)
      .catch(() => setEntityOptions([]));
  }, []);

  useEffect(() => {
    if (!selectedTenantId) return;
    let cancelled = false;
    setOverviewLoading(true);
    fetchSalesActivityDashboard(selectedTenantId, {
      startDatetime: toIsoOrNull(rangeStart),
      endDatetime: toIsoOrNull(rangeEnd),
      crmEntityId,
      salesUserId,
    })
      .then((res) => {
        if (cancelled) return;
        setOverview(res);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load activity data");
      })
      .finally(() => {
        if (!cancelled) setOverviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenantId, rangeStart, rangeEnd, crmEntityId, salesUserId]);

  useEffect(() => {
    if (!selectedTenantId) return;
    let cancelled = false;
    setFilteredLoading(true);
    fetchSalesActivityDashboard(selectedTenantId, {
      startDatetime: toIsoOrNull(rangeStart),
      endDatetime: toIsoOrNull(rangeEnd),
      crmEntityId,
      salesUserId,
      crmSalesActivityTypeId,
      crmSalesActivityTypeStatusId,
    })
      .then((res) => {
        if (!cancelled) setFiltered(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load activity data");
      })
      .finally(() => {
        if (!cancelled) setFilteredLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    selectedTenantId,
    rangeStart,
    rangeEnd,
    crmEntityId,
    crmSalesActivityTypeId,
    crmSalesActivityTypeStatusId,
    salesUserId,
  ]);

  const handleSelectTypeFromChart = (typeId: number) => {
    setCrmSalesActivityTypeId((prev) => (prev === typeId ? null : typeId));
    setCrmSalesActivityTypeStatusId(null);
  };

  const handleTypeDropdownChange = (typeId: number | null) => {
    setCrmSalesActivityTypeId(typeId);
    setCrmSalesActivityTypeStatusId(null);
  };

  const handleSelectStatusFromChart = (statusId: number) => {
    setCrmSalesActivityTypeStatusId((prev) => (prev === statusId ? null : statusId));
  };

  // Used where a single click picks a type AND its status together (the
  // stacked-by-status bar chart). Clicking the exact same segment again
  // clears both — chaining the two toggle handlers above independently
  // would leave status re-set right after type clears it, since each only
  // knows about its own previous value.
  const handleSelectSegment = (typeId: number, statusId?: number) => {
    const isSameSelection =
      crmSalesActivityTypeId === typeId &&
      (statusId ? crmSalesActivityTypeStatusId === statusId : true);
    if (isSameSelection) {
      setCrmSalesActivityTypeId(null);
      setCrmSalesActivityTypeStatusId(null);
    } else {
      setCrmSalesActivityTypeId(typeId);
      setCrmSalesActivityTypeStatusId(statusId ?? null);
    }
  };

  const handleClearFilters = () => {
    setDateRange([null, null]);
    setCrmEntityId(null);
    setCrmSalesActivityTypeId(null);
    setCrmSalesActivityTypeStatusId(null);
  };

  const overviewTypes = overview?.crm_sales_activity_type_counts || [];

  // Status breakdown always reads from the entity/date-scoped overview
  // (which already carries every type's full status list), so it renders
  // instantly and never depends on the separately-filtered request below.
  const selectedTypeForChart =
    overviewTypes.find((t) => t.crm_sales_activity_type_id === crmSalesActivityTypeId) ||
    null;

  const describeActivityFilters = () => {
    const parts: string[] = [];
    const rangeText = `${dayjs(rangeStart).format("DD MMM YYYY")} - ${dayjs(rangeEnd).format("DD MMM YYYY")}`;
    parts.push(hasCustomDateRange ? `Date Range: ${rangeText}` : `Date Range: ${DEFAULT_RANGE_LABEL} (${rangeText})`);
    const entity = entityOptions.find((item) => item.crm_entity_id === crmEntityId);
    if (entity) parts.push(`CRM Entity: ${entity.crm_entity_display_name}`);
    const type = overviewTypes.find((item) => item.crm_sales_activity_type_id === crmSalesActivityTypeId);
    if (type) parts.push(`Activity Type: ${type.crm_sales_activity_type_display_name}`);
    const status = type?.statuses?.find(
      (item) => item.crm_sales_activity_type_status_id === crmSalesActivityTypeStatusId
    );
    if (status) parts.push(`Status: ${status.crm_sales_activity_type_status_display_name}`);
    return parts.length ? parts.join(" · ") : "No filters applied";
  };

  const handleExport = async (kind: ExportKind) => {
    const container = reportRef.current;
    if (!selectedTenantId || exporting) return;
    setExporting(kind);
    try {
      if (kind === "pdf") {
        if (!container) return;
        await new Promise((resolve) => setTimeout(resolve, 150));
        await exportDashboardPdf({
          container,
          sectionSelector: ".ar-pdf-section",
          title: "Activities Report",
          filtersText: describeActivityFilters(),
          fileName: `activities-report-${dayjs().format("YYYY-MM-DD")}.pdf`,
        });
      } else {
        const file = await exportSalesActivityExcel(selectedTenantId, exportFilters);
        await downloadExportFile(file, `activities-report-${dayjs().format("YYYY-MM-DD")}`);
      }
    } catch (err) {
      setError((err as Error)?.message || `Failed to export ${kind === "pdf" ? "PDF" : "Excel"}`);
    } finally {
      setExporting(null);
    }
  };

  const selectedEntity = entityOptions.find((item) => item.crm_entity_id === crmEntityId);
  const selectedType = overviewTypes.find(
    (item) => item.crm_sales_activity_type_id === crmSalesActivityTypeId
  );
  const selectedStatus = selectedType?.statuses?.find(
    (item) => item.crm_sales_activity_type_status_id === crmSalesActivityTypeStatusId
  );

  const filterChips: FilterChipItem[] = [
    hasCustomDateRange
      ? {
          key: "date",
          icon: "ti ti-calendar",
          label: "Date",
          value: formatCompactRange(rangeStart, rangeEnd),
          onRemove: () => setDateRange([null, null]),
        }
      : {
          key: "default-date",
          icon: "ti ti-calendar",
          label: DEFAULT_RANGE_LABEL,
          value: formatCompactRange(defaultRange[0], defaultRange[1]),
          isDefault: true,
        },
  ];
  if (crmEntityId !== null) {
    filterChips.push({
      key: "entity",
      icon: "ti ti-building",
      label: "CRM Entity",
      value: selectedEntity?.crm_entity_display_name ?? String(crmEntityId),
      onRemove: () => setCrmEntityId(null),
    });
  }
  if (crmSalesActivityTypeId !== null) {
    filterChips.push({
      key: "type",
      icon: "ti ti-category",
      label: "Activity Type",
      value:
        selectedType?.crm_sales_activity_type_display_name ?? String(crmSalesActivityTypeId),
      onRemove: () => handleTypeDropdownChange(null),
    });
  }
  if (crmSalesActivityTypeStatusId !== null) {
    filterChips.push({
      key: "status",
      icon: "ti ti-progress-check",
      label: "Status",
      value:
        selectedStatus?.crm_sales_activity_type_status_display_name ??
        String(crmSalesActivityTypeStatusId),
      onRemove: () => setCrmSalesActivityTypeStatusId(null),
    });
  }

  const entityBreakdownRows = flattenActivityByEntity(
    filtered?.crm_sales_activity_type_statuses_by_crm_entity?.length
      ? filtered.crm_sales_activity_type_statuses_by_crm_entity
      : filtered?.crm_sales_activity_types_by_crm_entity
  );

  if (!selectedTenantId) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="ti ti-building-community fs-1 text-muted mb-3" />
                  <h5>Select a Tenant</h5>
                  <p className="text-muted mb-0">
                    Please select a tenant to view the activities report.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper activities-report">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center row-gap-2">
                <div className="col">
                  <div className="d-flex align-items-center flex-wrap gap-3">
                    <h3 className="page-title d-flex align-items-center mb-0">
                      Activities Report
                    </h3>
                    <FilterChips
                      chips={filterChips}
                      onClearAll={handleClearFilters}
                      disabled={overviewLoading || filteredLoading}
                      className="mb-0"
                    />
                  </div>
                </div>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex title-head justify-content-end align-items-center gap-2">
                    <ExportDropdown
                      disabled={!overview || overviewLoading || filteredLoading}
                      exporting={exporting}
                      spinClassName="ar-spin"
                      onExport={handleExport}
                    />
                    <div className="daterange-picker d-flex align-items-center justify-content-center">
                      <div className="head-icons mb-0">
                        <CollapseHeader />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <ActivitiesReportFilters
          loading={overviewLoading || filteredLoading}
          dateRange={dateRange}
          onDateRangeChange={(start, end) => setDateRange([start, end])}
          entityOptions={entityOptions}
          crmEntityId={crmEntityId}
          onEntityChange={setCrmEntityId}
          typeOptions={overviewTypes}
          crmSalesActivityTypeId={crmSalesActivityTypeId}
          onTypeChange={handleTypeDropdownChange}
          crmSalesActivityTypeStatusId={crmSalesActivityTypeStatusId}
          onStatusChange={setCrmSalesActivityTypeStatusId}
          onClear={handleClearFilters}
        />

        <div ref={reportRef}>
          <div className="ar-pdf-section">
            <ActivityScoreCards types={[...overviewTypes].sort((a, b) => b.counts - a.counts)} loading={overviewLoading} />
          </div>

          <div className="row row-gap-3 ar-pdf-section">
            <div className="col-xl-6 d-flex">
              <div className="card flex-fill mb-4 ar-chart-card">
                <div className="card-header">
                  <span className="ar-chart-icon">
                    <i className="ti ti-chart-bar" />
                  </span>
                  <h5 className="mb-0">Activities by Type &amp; Status</h5>
                </div>
                <div className="card-body">
                  {overviewLoading ? (
                    <ChartLoadingSkeleton variant="bars" height={340} />
                  ) : (
                    <ActivityTypeChart
                      types={overviewTypes}
                      onSelectSegment={handleSelectSegment}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-6 d-flex">
              <div className="card flex-fill mb-4 ar-chart-card">
                <div className="card-header">
                  <span className="ar-chart-icon">
                    <i className="ti ti-chart-donut" />
                  </span>
                  <h5 className="mb-0">
                    {selectedTypeForChart
                      ? `Status Breakdown — ${selectedTypeForChart.crm_sales_activity_type_display_name}`
                      : "Activity Type Distribution"}
                  </h5>
                </div>
                <div className="card-body">
                  {overviewLoading ? (
                    <ChartLoadingSkeleton variant="donut" height={320} />
                  ) : (
                    <ActivityStatusChart
                      types={overviewTypes}
                      selectedTypeId={crmSalesActivityTypeId}
                      onSelectType={handleSelectTypeFromChart}
                      onClearType={() => handleTypeDropdownChange(null)}
                      selectedStatusId={crmSalesActivityTypeStatusId}
                      onSelectStatus={handleSelectStatusFromChart}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row row-gap-3 ar-pdf-section">
            <div className="col-12 d-flex">
              <div className="card flex-fill mb-4 ar-chart-card">
                <div className="card-header">
                  <span className="ar-chart-icon">
                    <i className="ti ti-chart-histogram" />
                  </span>
                  <h5 className="mb-0">
                    {selectedTypeForChart
                      ? `${selectedTypeForChart.crm_sales_activity_type_display_name} Status × CRM Entity`
                      : "Activity Type × CRM Entity"}
                  </h5>
                </div>
                <div className="card-body">
                  {filteredLoading && entityBreakdownRows.length === 0 ? (
                    <ChartLoadingSkeleton variant="bars" height={340} />
                  ) : (
                    // Deliberately not gated on `filteredLoading` once it has
                    // real rows: unmounting an ApexCharts instance the instant
                    // a user clicks into it (which is exactly when a refetch
                    // starts) tears the chart down while it may still be
                    // mid-hover/click, which crashes it. Keeping it mounted
                    // and just letting its props update in place is what
                    // actually keeps drill-down clicks safe.
                    <ActivityEntityBreakdownChart
                      rows={entityBreakdownRows}
                      types={overviewTypes}
                      selectedTypeId={crmSalesActivityTypeId}
                      selectedStatusId={crmSalesActivityTypeStatusId}
                      onSelectType={handleSelectTypeFromChart}
                      onSelectStatus={handleSelectStatusFromChart}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivitiesReport;
